import React, { useCallback, useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Chip,
  CircularProgress,
  Modal,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography
} from '@mui/material';
import GenericButton, { GenericButtonTypeEnum } from '../../../components/GenericButton';
import { CommonModalContentBox, CommonModalWrapperBox } from '../../../common/styles/Common.styles';
import { MESSAGE_TYPE } from '../../../common/constants/common.constants';
import { createNotification } from '../../../utilities/helper/notification.helper';
import { formatMoney } from '../../../utilities/helper/money.helper';
import { ChildBillingProfileDto } from '../../../common/interfaces/invoicing.interface';
import { getChildBillingProfileHistory } from '../../../api/invoicing.api';
import { useTranslation } from 'react-i18next';

interface ChildBillingHistoryModalProps {
  kindergartenId: string;
  childId: string;
  childName: string;
  onClose: () => void;
}

/**
 * Every arrangement this child has been billed under, newest first.
 *
 * Arrangements are dated rather than edited in place, precisely so an invoice issued
 * last spring can still be explained. Until now the panel kept that history and showed
 * none of it, which left an admin looking at an old invoice with no way to find out
 * what it was priced from.
 */
const ChildBillingHistoryModal: React.FC<ChildBillingHistoryModalProps> = ({ kindergartenId, childId, childName, onClose }) => {
  const { t } = useTranslation();
  const [profiles, setProfiles] = useState<ChildBillingProfileDto[]>([]);
  const [loader, setLoader] = useState(true);

  const load = useCallback(async () => {
    setLoader(true);
    try {
      const result = await getChildBillingProfileHistory(kindergartenId, childId);
      setProfiles(result.results ?? []);
    } catch {
      createNotification(MESSAGE_TYPE.ERROR, t('billing.history.loadFailed'));
    } finally {
      setLoader(false);
    }
  }, [kindergartenId, childId, t]);

  useEffect(() => {
    load();
  }, [load]);

  return (
    <Modal open onClose={onClose}>
      <CommonModalWrapperBox sx={{ width: 900, maxHeight: '90vh', overflowY: 'auto' }}>
        <CommonModalContentBox>
          <Typography variant="h2" sx={{ fontSize: 22, mb: 1 }}>
            {t('billing.history.title', { name: childName })}
          </Typography>
          <Typography sx={{ color: 'text.secondary', fontSize: 13, mb: 3 }}>{t('billing.history.explanation')}</Typography>

          {loader ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
              <CircularProgress />
            </Box>
          ) : !profiles.length ? (
            <Alert severity="info" sx={{ borderRadius: 2 }}>
              {t('billing.history.none')}
            </Alert>
          ) : (
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell sx={{ width: '14%' }}>{t('dateRange.from')}</TableCell>
                  <TableCell sx={{ width: '14%' }}>{t('dateRange.to')}</TableCell>
                  <TableCell sx={{ width: '18%' }}>{t('billing.arrangement.pricedBy')}</TableCell>
                  <TableCell sx={{ textAlign: 'right', width: '16%' }}>{t('billing.history.monthlyFixedNet')}</TableCell>
                  <TableCell>{t('common.note')}</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {profiles.map(profile => (
                  <TableRow key={profile.id}>
                    <TableCell>{profile.validFrom}</TableCell>
                    <TableCell>
                      {profile.validTo ? (
                        profile.validTo
                      ) : (
                        // validTo is exclusive, so an open arrangement has none at all
                        <Chip size="small" color="success" variant="outlined" label={t('billing.history.inForce')} />
                      )}
                    </TableCell>
                    <TableCell>
                      {profile.items.length
                        ? t('billing.history.ownItems', { count: profile.items.length })
                        : profile.billingSchemeName
                          ? t('billing.history.scheme', { name: profile.billingSchemeName })
                          : '—'}
                    </TableCell>
                    <TableCell sx={{ textAlign: 'right' }}>{formatMoney(profile.monthlyFixedNet, { withCurrency: true })}</TableCell>
                    <TableCell sx={{ fontSize: 13, color: 'text.secondary' }}>{profile.note ?? '—'}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}

          <Stack direction="row" justifyContent="flex-end" sx={{ mt: 3 }}>
            <GenericButton buttonType={GenericButtonTypeEnum.Neutral} variant="outlined" onClick={onClose} label={t('common.close')} />
          </Stack>
        </CommonModalContentBox>
      </CommonModalWrapperBox>
    </Modal>
  );
};

export default ChildBillingHistoryModal;
